// The one-click priority toggle that sits on an RFQ row.
//
// PRESENTATIONAL ONLY. It takes state and a callback; the owning list holds the
// mutation. That keeps RfqRow's "no data fetching, no mutations" rule intact
// when this is passed into its slot.
//
// WHY IT IS A SIBLING OF THE ROW LINK, NEVER A CHILD.
// See the comment block at the top of rfq-row.tsx: a <button> inside an <a> is
// invalid HTML, browsers resolve it inconsistently, and this exact row already
// shipped a bug from nested interactive elements (efa5c4f — "only part of an
// RFQ row was clickable"). Rendering through the row's slot keeps it a sibling,
// so no stopPropagation is needed and navigation is never triggered by accident.
//
// WHY FAILURE IS VISIBLE HERE.
// The mutation rolls the cache back on error, which means a failed toggle looks
// EXACTLY like the operator changing their mind — the flag simply returns to
// where it was. On a flaky connection you would believe you had prioritised
// something you had not. The detail pane has always said "Couldn't update flag
// — try again"; a more convenient control that quietly dropped that guarantee
// would be a worse control, however many clicks it saves.
//
// The vocabulary is deliberately the BOARD's vocabulary ("priority"), not the
// column's ("flag"). The tab is labelled 🚩 Priority, so a control labelled
// "flag follow-up" reads as a different feature — which is exactly how the
// detail-pane button went unfound.

interface Props {
  isFlagged: boolean;
  /** Disables the control and dims it while the PATCH is in flight. */
  isPending?: boolean;
  /** True only for the row whose own toggle failed — see rfq-list.tsx. */
  isError?: boolean;
  onToggle: () => void;
}

export function FlagToggle({ isFlagged, isPending = false, isError = false, onToggle }: Props) {
  const action = isFlagged ? 'Remove from priority board' : 'Add to priority board';
  // The failure has to reach BOTH audiences. Sighted users get the colour and
  // the glyph; screen-reader users get it in the accessible name, because a
  // red tint announces as nothing at all.
  const label = isError ? `${action} — last attempt failed, try again` : action;

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={isFlagged}
      disabled={isPending}
      onClick={onToggle}
      className={
        // A generous, full-height hit area on the trailing edge. Small targets
        // are the reason row-level toggles get mis-tapped on touch, and the
        // drag handle already owns the leading edge on the board.
        'shrink-0 w-11 flex items-center justify-center self-stretch text-base ' +
        'border-l transition-colors disabled:opacity-40 ' +
        (isError
          ? 'border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/20'
          : isFlagged
            ? 'border-border text-orange-600 hover:bg-muted'
            : 'border-border text-muted-foreground opacity-40 hover:opacity-100 hover:bg-muted')
      }
    >
      {isError ? '⚠' : isFlagged ? '🚩' : '⚐'}
    </button>
  );
}
